/**
 * `DestructiveCaseDialog` — the typed confirmation guarding case RETIREMENT.
 *
 * Operator directive 2026-07-19: *"delete — even the soft-delete — must be
 * gated; users may not trigger it inadvertently."* Retiring is reversible,
 * but it removes a matter from the lawyer's working view, so it must be a
 * deliberate act rather than a mis-click on a row.
 *
 * The action stays disabled until the exact token is typed, shown in the
 * USER'S OWN LANGUAGE and matched exactly as the server matches it (trim
 * outer whitespace, no case folding) — deliberateness is the feature.
 *
 * ⚠ This gate is UI-ONLY. `case.delete` takes no confirmation argument, so
 * unlike the org-admin check (server-side, fail-closed) this dialog is the
 * only barrier against an accidental retire. Do not describe it as a
 * security control.
 *
 * There is no purge variant: permanent destroy is blocked in ALL affordances
 * for v1 (operator ruling 2026-07-19).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  confirmationTokenForLocale,
  isConfirmationTokenValid,
} from "@/api/lx-recycle-bin";

export interface DestructiveCaseDialogProps {
  /** The live case being retired. */
  target: { title: string | null; handle: string };
  onCancel: () => void;
  onConfirm: (typed: string) => Promise<void>;
}

export function DestructiveCaseDialog({
  target,
  onCancel,
  onConfirm,
}: DestructiveCaseDialogProps) {
  const { t, i18n } = useTranslation();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = confirmationTokenForLocale(i18n.language);
  const armed = isConfirmationTokenValid(typed, i18n.language) && !busy;

  // Escape cancels — but only while nothing is in flight, so a purge is
  // never abandoned mid-request with the outcome unknown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const submit = async () => {
    if (!armed) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(typed.trim());
    } catch (err) {
      // Server messages are already user-facing pt-BR — surface verbatim.
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="destructive-dialog-title"
      data-testid="destructive-case-dialog"
    >
      <div className={cn(
          "w-full max-w-md rounded-lg border bg-white p-5 shadow-xl dark:bg-coffee-950",
          "border-amber-300/60 dark:border-amber-900/50",
        )}>
        <div className="flex items-start gap-2">
          <AlertTriangle
            size={18}
            className="mt-0.5 shrink-0 text-amber-500"
            aria-hidden
          />
          <h2
            id="destructive-dialog-title"
            className="text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            {t("recycleBin.deleteDialog.title")}
          </h2>
        </div>

        <p className="mt-3 text-[13px] leading-snug text-slate-600 dark:text-slate-300">
          {t("recycleBin.deleteDialog.body")}
        </p>

        {/* Name what is about to be destroyed — or say plainly that we
            cannot, rather than showing a derived stand-in. */}
        <div className="mt-3 rounded border border-cream-200 bg-cream-50/60 px-3 py-2 text-[12px] dark:border-coffee-800 dark:bg-coffee-900/40">
          {target.title ? (
            <span className="font-medium text-slate-800 dark:text-slate-100">
              {target.title}
            </span>
          ) : (
            <span
              data-testid="destructive-dialog-unreadable"
              className="font-medium text-amber-700 dark:text-amber-400"
            >
              {t("recycleBin.unreadableEntry")}
            </span>
          )}
          <div className="mt-0.5 break-all font-mono text-[10px] text-slate-400 dark:text-slate-500">
            {target.handle}
          </div>
        </div>

        <label
          htmlFor="destructive-confirm-input"
          className="mt-4 block text-[12px] text-slate-600 dark:text-slate-300"
        >
          {t("recycleBin.deleteDialog.typeToConfirm")}{" "}
          <code className="rounded bg-cream-200 px-1 py-0.5 font-mono text-[11px] text-slate-800 dark:bg-coffee-800 dark:text-slate-100">
            {token}
          </code>
        </label>
        <input
          id="destructive-confirm-input"
          data-testid="destructive-confirm-input"
          type="text"
          autoComplete="off"
          autoFocus
          value={typed}
          disabled={busy}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
          className="mt-1.5 w-full rounded border border-cream-300 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-red-400 dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
        />

        {error && (
          <div
            data-testid="destructive-dialog-error"
            role="alert"
            className="mt-2 rounded border border-red-200/70 bg-red-50 px-2 py-1.5 text-[11px] text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            data-testid="destructive-cancel"
            onClick={onCancel}
            disabled={busy}
            className="rounded px-3 py-1.5 text-[12px] text-slate-600 hover:bg-cream-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-coffee-800"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            data-testid="destructive-confirm"
            onClick={() => void submit()}
            disabled={!armed}
            aria-disabled={!armed}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-[12px] font-medium text-white",
              armed
                ? "bg-amber-600 hover:bg-amber-700"
                : "cursor-not-allowed bg-amber-300 dark:bg-amber-900/50",
            )}
          >
            {busy && <Loader2 size={12} className="animate-spin" aria-hidden />}
            {t("recycleBin.deleteDialog.confirmAction")}
          </button>
        </div>
      </div>
    </div>
  );
}
